/**
 * D-EZ-111 — THE STAKES CARD'S MEMORY (`StakesContext.pairNote` / `driverNote`).
 *
 * D-EZ-104 left a seam on the stakes card for the rivalry engine and the
 * personalities: one line about two drivers ("Ackerd wrecked him here last
 * season") and one about a driver ("Ballew has a short fuse"). These are built
 * from the booth's league — the people layer CUT AT THE PREVIOUS RACE — so a
 * card never knows about anything that happened in the race on the table.
 *
 * Narrative only: hashes of stable ids, no dice, nothing stored.
 */

import type { BoothLeague } from './league.ts';
import { pairHistory, citedMoment } from './storylines.ts';
import { rivalryBetween } from '../people/rivalry.ts';
import { personalityOf } from '../people/context.ts';
import { surname } from './kit.ts';

export interface StakesNotes {
  pairNote: (a: string, b: string) => string | null;
  driverNote: (driverId: string) => string | null;
}

/** Trait words (people/personality.ts) as a sentence about him. Temper and nerve first: those are what a pivotal roll is about. */
const TRAIT_LINES: [string, string][] = [
  ['Short fuse', '{s} has a short fuse.'],
  ['Runs hot', '{s} runs hot.'],
  ['Ice in his veins', '{s} has ice in his veins.'],
  ['Cool head', '{s} keeps a cool head.'],
  ["Thinks he's the best out there", "{s} thinks he's the best out there."],
  ['Doubts himself', '{s} doubts himself.'],
  ['Second-guesses himself', '{s} second-guesses himself.'],
  ['Wants it all', '{s} wants it all.'],
  ['Hungry', '{s} is hungry.'],
];

export function stakesNotes(league: BoothLeague | null | undefined): StakesNotes | null {
  const people = league?.people;
  if (!league || !people) return null;
  const nameOf = (id: string): string => {
    const e = people.registry.byId[id];
    if (e) return e.name;
    for (let i = people.seasons.length - 1; i >= 0; i--) {
      const c = people.seasons[i].roster.find((x) => x.driverId === id);
      if (c) return c.name;
    }
    return id;
  };
  const now = { seasonNumber: league.seasonNumber, raceNumber: league.raceNumber };
  const venue = league.race?.venue ?? null;
  const seq = (league.seasonNumber - 1) * Math.max(1, league.seasonRaces) + league.raceNumber;
  return {
    pairNote(a, b) {
      const r = rivalryBetween(people.book, a, b);
      if (!r || !citedMoment(r, venue)) return null;
      if (r.state !== 'boiling' && r.state !== 'hot' && r.state !== 'simmering') return null;
      return pairHistory(r, nameOf, now, venue, `stakes|${league.key}`, seq)?.text ?? null;
    },
    driverNote(id) {
      const p = personalityOf(id, people);
      for (const [trait, line] of TRAIT_LINES) {
        if (p.traits.includes(trait)) return line.replace('{s}', surname(nameOf(id)));
      }
      return null;
    },
  };
}
